import { createAsyncThunk, createSelector, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { type UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { messagingApi } from "messaging/_api/messaging-api";
import ForumQuerySchema, { type ForumQueryJSON } from "messaging/forums/_api/queries/_schemas/forum-query-schema";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import { forumUnreadCountReported } from "messaging/forums/_stores/forum-unread-counts-redux-slice";
import Topic from "messaging/forums/topics/topic";
import Session from "users/__session/session";

type ForumStoreState = {
  byId: {
    entries: Record<string, ForumJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byTopic: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, ForumJSON[]>;
    /** Every forum the query matches, from X-Pagination — not the length of the page in `entries`. */
    totalElements: Record<string, number>;
    /**
     * How many times a reader's unread count has moved while a list of unread conversations was held
     * or loading (#6583). Each move can add a conversation to the list or take one away.
     */
    invalidations: Record<string, number>;
    /** The invalidation count when the request on the wire was sent. */
    requestedAtInvalidation: Record<string, number>;
    /**
     * The invalidation count the rows on screen were asked at. The list is stale while this is
     * behind `invalidations`, so a response sent before a newer move cannot mark the list current.
     */
    answeredAtInvalidation: Record<string, number>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, ForumQueryJSON>;
  };
};

/**
 * What a forum lookup records when the API did not return the forum: it is gone, or it exists and
 * this viewer may not read it. The bare "Forum not found" is left for an id nobody answered for.
 */
export const unavailableForumTexts: Record<UnavailableReason, string> = {
  missing: "Forum no longer exists",
  unauthorized: "No access to this forum",
};

function unavailableForumText(reason?: UnavailableReason): string {
  return reason ? unavailableForumTexts[reason] : "Forum not found";
}

const initialState: ForumStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byTopic: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    totalElements: {},
    invalidations: {},
    requestedAtInvalidation: {},
    answeredAtInvalidation: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populateForums = createAsyncThunk<
  ForumJSON[],
  {
    session: Session;
    forumIds: Guid[];
  }
>(
  "forums/getForumsByIds",
  async (
    {
      forumIds,
    }: {
      session: Session;
      forumIds: Guid[];
    },
    thunkAPI
  ) => {
    if (!forumIds.length) return [];

    const store: { forums: ForumStoreState } = thunkAPI.getState() as {
      forums: ForumStoreState;
    };
    const missingForumsIds = _.uniqWith(
      forumIds.filter((forumId) => !store.forums.byId.entries[forumId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingForumsIds.length) return [];

    const request = thunkAPI.dispatch(messagingApi.endpoints.getForumsByIds.initiate({ ids: missingForumsIds }));
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return (await request.unwrap()).found;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  }
);

export const populateForumsByTopic = createAsyncThunk(
  "forums/getForumsByTopic",
  async (
    {
      topic,
      forceRefetch = false,
    }: {
      session: Session;
      topic: Topic;
      /** Ask the server again even when a cached answer for this topic is still fresh. */
      forceRefetch?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const filterParameters = new ForumFilterParameters({
        context: topic.context,
        entityClass: topic.entityClass,
        entityId: topic.entityId,
      });
      const orderParameters = new ForumOrderParameters({
        orderBy: ForumOrderField.CreatedDate,
        orderDir: OrderDirection.Descending,
      });
      const paginationParameters = PaginationParameters.create({
        pageIndex: 0,
        pageSize: 100,
      });
      const request = new ForumQuery({
        filterParameters,
        orderParameters,
        paginationParameters,
      });
      const apiRequest = thunkAPI.dispatch(
        messagingApi.endpoints.getForums.initiate(
          {
            query: request,
            storeTarget: { kind: "byTopic", topicKey: topic.toString() },
          },
          { forceRefetch }
        )
      );
      const onAbort = () => apiRequest.abort();
      thunkAPI.signal.addEventListener("abort", onAbort);
      try {
        return await apiRequest.unwrap();
      } finally {
        thunkAPI.signal.removeEventListener("abort", onAbort);
        apiRequest.unsubscribe();
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const populateForumsByQuery = createAsyncThunk<
  ForumJSON[],
  {
    session: Session;
    query: ForumQuery;
  }
>(
  "forums/getForumsByQuery",
  async (
    {
      query,
    }: {
      session: Session;
      query: ForumQuery;
    },
    thunkAPI
  ) => {
    // A list of unread conversations is asked again when it went stale, so the cached answer from
    // before the count moved cannot stand in for it.
    const request = thunkAPI.dispatch(
      messagingApi.endpoints.getForums.initiate(
        {
          query,
          storeTarget: { kind: "byQuery", queryKey: query.searchParams.toString() },
        },
        { forceRefetch: Boolean(query.filterParameters.unread) }
      )
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  }
);

const forumsSlice = createSlice({
  name: "forums",
  initialState,
  reducers: {
    addForum: (state, action: PayloadAction<Forum>) => {
      if (action.payload.id) {
        state.byId.entries[action.payload.id.value] = action.payload.toJSON();
      }
      if (action.payload.topic) {
        const topicKey = action.payload.topic.toString();
        if (!state.byTopic.entries[topicKey]) {
          state.byTopic.entries[topicKey] = [];
        }
        state.byTopic.entries[topicKey].push(action.payload.toJSON());
      }
    },
    updateForumInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: ForumQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;
      state.interfaceQueries.entries[interfaceName] = ForumQuerySchema.serialize(query);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchPending, (state, action) => {
      for (const id of action.meta.arg.originalArgs.ids) {
        const forumId = id instanceof Guid ? id.value : id;
        state.byId.loading[forumId] = true;
        state.byId.error[forumId] = null;
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchFulfilled, (state, action) => {
      for (const forum of action.payload.found) {
        if (!forum.id) continue;
        state.byId.entries[forum.id] = forum;
      }
      const reasons: Record<string, UnavailableReason> = {};
      for (const { id, reason } of action.payload.unavailable) reasons[id] = reason;
      for (const id of action.meta.arg.originalArgs.ids) {
        const forumId = id instanceof Guid ? id.value : id;
        state.byId.loading[forumId] = false;
        state.byId.error[forumId] = state.byId.entries[forumId] ? null : unavailableForumText(reasons[forumId]);
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchRejected, (state, action) => {
      for (const id of action.meta.arg.originalArgs.ids) {
        const forumId = id instanceof Guid ? id.value : id;
        state.byId.loading[forumId] = false;
        state.byId.error[forumId] = action.error.message || "Error fetching forum info.";
      }
    });

    builder.addMatcher(messagingApi.endpoints.getForums.matchPending, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind === "byTopic") {
        state.byTopic.loading[storeTarget.topicKey] = true;
        state.byTopic.error[storeTarget.topicKey] = null;
      }
      if (storeTarget?.kind === "byQuery") {
        state.byQuery.loading[storeTarget.queryKey] = true;
        state.byQuery.error[storeTarget.queryKey] = null;
        state.byQuery.requestedAtInvalidation[storeTarget.queryKey] =
          state.byQuery.invalidations[storeTarget.queryKey] ?? 0;
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForums.matchFulfilled, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      for (const forum of action.payload) {
        if (!forum.id) continue;
        state.byId.entries[forum.id] = forum;
        state.byId.loading[forum.id] = false;
        state.byId.error[forum.id] = null;
      }
      if (storeTarget?.kind === "byTopic") {
        state.byTopic.entries[storeTarget.topicKey] = action.payload;
        state.byTopic.loading[storeTarget.topicKey] = false;
        state.byTopic.error[storeTarget.topicKey] = null;
      }
      if (storeTarget?.kind === "byQuery") {
        state.byQuery.entries[storeTarget.queryKey] = action.payload;
        // The body is one page. How many forums the query matches across every page travels in
        // X-Pagination, which the endpoint's transform does not keep, so it is read here. A
        // response without the header records no total rather than a total of zero.
        const paginationHeader = readPaginationHeader(action.meta.baseQueryMeta);
        if (paginationHeader) {
          state.byQuery.totalElements[storeTarget.queryKey] = PaginatedResponse.from(action.payload, paginationHeader)
            .totalElements;
        } else {
          delete state.byQuery.totalElements[storeTarget.queryKey];
        }
        state.byQuery.loading[storeTarget.queryKey] = false;
        state.byQuery.error[storeTarget.queryKey] = null;
        // Current only as of when it was asked: a move that arrived while it was on the wire leaves
        // the list stale, and the table asks again.
        state.byQuery.answeredAtInvalidation[storeTarget.queryKey] =
          state.byQuery.requestedAtInvalidation[storeTarget.queryKey] ?? 0;
        delete state.byQuery.requestedAtInvalidation[storeTarget.queryKey];
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForums.matchRejected, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind === "byTopic") {
        state.byTopic.loading[storeTarget.topicKey] = false;
        state.byTopic.error[storeTarget.topicKey] = action.error.message || "Failed to load forums for topic";
      }
      if (storeTarget?.kind === "byQuery") {
        state.byQuery.loading[storeTarget.queryKey] = false;
        state.byQuery.error[storeTarget.queryKey] = action.error.message || "Failed to load forums for query";
        delete state.byQuery.requestedAtInvalidation[storeTarget.queryKey];
      }
    });
    // Every way a reader's unread count moves: a realtime message event, a read or unread written
    // from a thread, and Mark all read. Any of them can add a conversation to the unread ones or
    // take one away (#6583).
    builder.addMatcher(
      isAnyOf(
        forumUnreadCountReported,
        messagingApi.endpoints.createBulkMessages.matchFulfilled,
        messagingApi.endpoints.markForumRead.matchFulfilled
      ),
      (state) => {
        const heldOrLoading = new Set([...Object.keys(state.byQuery.entries), ...Object.keys(state.byQuery.loading)]);
        for (const queryKey of heldOrLoading) {
          if (new URLSearchParams(queryKey).get("unread") !== "true") continue;
          state.byQuery.invalidations[queryKey] = (state.byQuery.invalidations[queryKey] ?? 0) + 1;
        }
      }
    );
    builder.addMatcher(messagingApi.endpoints.createForum.matchFulfilled, (state, action) => {
      upsertForumJSON(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.deleteForum.matchFulfilled, (state, action) => {
      const forumId = action.meta.arg.originalArgs.forumId;
      delete state.byId.entries[forumId];
      delete state.byId.loading[forumId];
      delete state.byId.error[forumId];
      removeForumFromLists(state.byTopic.entries, forumId);
      for (const queryKey of removeForumFromLists(state.byQuery.entries, forumId)) {
        const totalElements = state.byQuery.totalElements[queryKey];
        if (totalElements) state.byQuery.totalElements[queryKey] = totalElements - 1;
      }
    });
  },
});

function readPaginationHeader(baseQueryMeta: unknown): string | undefined {
  const response = (baseQueryMeta as { response?: { headers?: Headers } } | undefined)?.response;
  return response?.headers?.get("x-pagination") ?? undefined;
}

function upsertForumJSON(state: ForumStoreState, forum: ForumJSON): void {
  if (!forum.id) return;
  state.byId.entries[forum.id] = forum;
  const forumModel = Forum.fromJSON(forum);
  if (forumModel?.topic) {
    const topicKey = forumModel.topic.toString();
    state.byTopic.entries[topicKey] = [
      ...(state.byTopic.entries[topicKey] ?? []).filter((existingForum) => existingForum.id !== forum.id),
      forum,
    ];
  }
  state.interfaceQueries.entries = {};
  state.byQuery.entries = {};
  state.byQuery.totalElements = {};
  state.byQuery.invalidations = {};
  state.byQuery.requestedAtInvalidation = {};
  state.byQuery.answeredAtInvalidation = {};
}

/** Removes the forum from every list, and returns the keys of the lists that held it. */
function removeForumFromLists(entries: Record<string, ForumJSON[]>, forumId: string): string[] {
  const keysThatHeldIt: string[] = [];
  for (const key of Object.keys(entries)) {
    const remaining = entries[key].filter((forum) => forum.id !== forumId);
    if (remaining.length !== entries[key].length) keysThatHeldIt.push(key);
    entries[key] = remaining;
  }
  return keysThatHeldIt;
}

export const { addForum, updateForumInterfaceQuery } = forumsSlice.actions;

const selectForumsByTopic = createSelector(
  [(state: RootState) => state.forums.byTopic.entries, (_: RootState, topic: Topic) => topic.toString()],
  (entries, topicKey) => entries[topicKey]?.map((json) => Forum.fromJSON(json)) ?? undefined
);
export const getForumsByTopic = (topic: Topic) => (state: RootState) => selectForumsByTopic(state, topic);
export const getIsLoadingForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.loading[topic.toString()];
export const getErrorLoadingForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.error[topic.toString()];

const selectForumsByIds = createSelector(
  [(state: RootState) => state.forums.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => entries[id.value])
      .filter(Boolean)
      .map((json) => Forum.fromJSON(json));
  }
);
export const getForumsByIds = (ids: Guid[]) => (state: RootState) => selectForumsByIds(state, ids);

const selectLoadingForumIds = createSelector([(state: RootState) => state.forums.byId.loading], (loading) => {
  return Object.keys(loading)
    .filter((forumId) => loading[forumId])
    .map((forumId) => Guid.fromJSON(forumId) as Guid);
});
export const getLoadingForumIds = (state: RootState) => selectLoadingForumIds(state);

export const getAreErrorsLoadingForumsByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => state.forums.byId.error[id.value] !== null);

const selectForumsByQuery = createSelector(
  [(state: RootState) => state.forums.byQuery.entries, (_: RootState, query?: ForumQuery) => query],
  (entries, query) => {
    if (!query) return;
    return entries[query.searchParams.toString()]?.map((json) => Forum.fromJSON(json)) ?? undefined;
  }
);
export const getForumsByQuery = (query?: ForumQuery) => (state: RootState) => selectForumsByQuery(state, query);

const selectForumQueryByInterface = createSelector(
  [(state: RootState) => state.forums.interfaceQueries.entries, (_: RootState, interfaceName: string) => interfaceName],
  (entries, interfaceName) => {
    const queryJson = entries[interfaceName];
    if (!queryJson) return;
    return ForumQuery.fromJSON(queryJson) ?? undefined;
  }
);
export const getForumQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectForumQueryByInterface(state, interfaceName);

/** How many forums the query matches across every page, or undefined until its first page loads. */
export const getTotalForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return undefined;
  return state.forums.byQuery.totalElements[query.searchParams.toString()];
};
/** Whether a list of unread conversations has to be asked again because a count moved since it loaded. */
export const getIsStaleForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  const queryKey = query.searchParams.toString();
  return (state.forums.byQuery.invalidations[queryKey] ?? 0) > (state.forums.byQuery.answeredAtInvalidation[queryKey] ?? 0);
};
export const getIsLoadingForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  return state.forums.byQuery.loading[query.searchParams.toString()] ?? false;
};
export const getErrorLoadingForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  return state.forums.byQuery.error[query.searchParams.toString()] ?? false;
};

export type ForumError = {
  id: Guid;
  error: string;
};
const selectAllForumErrors = createSelector([(state: RootState) => state.forums.byId.error], (errors) => {
  return Object.keys(errors)
    .filter((forumId) => forumId && errors[forumId])
    .map((id) => {
      const error = errors[id];
      if (!error) return null;
      return { id: Guid.fromJSON(id) as Guid, error } as ForumError;
    })
    .filter((item): item is ForumError => item !== null);
});
export const getAllForumErrors = (state: RootState) => selectAllForumErrors(state);

export default forumsSlice;
